import React from "react";
import { ArrowRight, Home, TrendingUp, Users, KeyRound, CircleDollarSign } from "lucide-react";
import { Button } from "./ui/button";
import { BrandName } from "./BrandName";
import { IMAGES } from "../mock";

const FloatingPhoto = ({ src, alt, className, rotate, delay }) => (
  <div
    className={`pointer-events-none absolute hidden lg:block animate-fade-up ${className}`}
    style={{ animationDelay: delay }}
  >
    <div
      className="rounded-2xl bg-white p-2 shadow-xl shadow-slate-900/15 transition-transform duration-500 hover:rotate-0"
      style={{ transform: `rotate(${rotate}deg)` }}
    >
      <img src={src} alt={alt} className="h-32 w-44 rounded-xl object-cover xl:h-36 xl:w-52" loading="eager" />
    </div>
  </div>
);

const FloatingIcon = ({ icon: Icon, className, color, delay }) => (
  <span
    className={`pointer-events-none absolute hidden h-12 w-12 items-center justify-center rounded-full border bg-white shadow-lg shadow-slate-900/10 lg:flex animate-fade-up ${color} ${className}`}
    style={{ animationDelay: delay }}
  >
    <Icon className="h-5 w-5" />
  </span>
);

const Hero = ({ onGetStarted }) => (
  <section id="top" className="relative overflow-hidden bg-gradient-to-b from-white via-white to-[#F0FBF1] pt-[72px]" data-testid="hero-section">
    <div className="pointer-events-none absolute -top-24 right-1/4 h-96 w-96 rounded-full bg-[#3CC54F]/10 blur-3xl" />
    <div className="pointer-events-none absolute top-40 left-1/4 h-80 w-80 rounded-full bg-[#3CABDA]/10 blur-3xl" />

    <FloatingPhoto src={IMAGES.heroMain} alt="Two friends holding the keys to their new home" className="left-[4%] top-[46%]" rotate={-6} delay="200ms" />
    <FloatingPhoto src={IMAGES.roommates} alt="Two roommates moving into the home they bought together" className="right-[4%] top-[46%]" rotate={6} delay="300ms" />
    <FloatingIcon icon={Users} className="left-[8%] top-[14%]" color="border-[#3CC54F]/30 text-[#2FA33F]" delay="150ms" />
    <FloatingIcon icon={CircleDollarSign} className="right-[9%] top-[14%]" color="border-[#3CABDA]/30 text-[#3CABDA]" delay="250ms" />
    <FloatingIcon icon={KeyRound} className="left-[16%] top-[32%]" color="border-[#3CABDA]/30 text-[#3CABDA]" delay="350ms" />
    <FloatingIcon icon={TrendingUp} className="right-[17%] top-[32%]" color="border-[#3CC54F]/30 text-[#2FA33F]" delay="400ms" />

    <div className="relative mx-auto flex max-w-4xl flex-col items-center px-5 pb-24 pt-16 text-center lg:pb-32 lg:pt-24 animate-fade-up">
      <span className="inline-flex items-center gap-2 rounded-full border border-[#3CC54F]/40 bg-white px-4 py-1.5 font-heading text-sm font-bold text-[#25862F] shadow-sm">
        <Home className="h-3.5 w-3.5" />
        <BrandName tm />
      </span>
      <h1 className="mt-6 font-heading text-[40px] font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-[54px] lg:text-[62px]">
        More Home. More Choices.
        <br />
        More Possibilities.
      </h1>
      <p className="mt-6 font-heading text-lg font-bold text-[#2FA33F] md:text-xl" data-testid="hero-subheading">
        Greater homebuying power, together.
      </p>
      <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-600">
        <BrandName tm /> gives you greater homebuying power, opening the door to more homes, better
        choices, and more of what you really want. Expand your search, explore homes you may not
        have considered before, and choose the home that better fits your lifestyle and your future.
      </p>
      <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
        <Button
          data-testid="hero-get-started-btn"
          onClick={onGetStarted}
          className="group h-auto rounded-full bg-[#3CC54F] px-8 py-4 text-base font-semibold text-white shadow-lg shadow-[#3CC54F]/30 transition-colors duration-200 hover:bg-[#2FA33F]"
        >
          Get Started
          <ArrowRight className="ml-1 h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
        </Button>
        <a
          href="#calculator"
          className="text-base font-semibold text-slate-700 underline decoration-[#3CABDA] decoration-2 underline-offset-4 transition-colors duration-200 hover:text-[#3CABDA]"
        >
          Try the Calculator
        </a>
      </div>
      <p className="mt-6 text-sm text-slate-500">Make big moves, together. Sign up today — it's free.</p>
    </div>
  </section>
);

export default Hero;
